import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { Star, Check, ShieldCheck, MessageSquare, Flame, BadgeCheck, User, Building2, Sun, Heart, Navigation, Share2, Activity, Clock, Phone, Trophy, CreditCard, Landmark, Banknote, Power, PowerOff, Pencil } from "lucide-react";
import { AmenityIcon } from "@/components/AmenityIcon";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { formatHour, formatPrice } from "@/lib/utils";
import { getImageUrl } from "@/lib/images";
import BackButton from "@/components/BackButton";
import DatePicker from "@/components/DatePicker";
import { useTheme } from "@/context/ThemeContext";
import VerifyPhone from "@/pages/VerifyPhone";

const voltIcon = L.divIcon({
  className: "",
  html: `<div style="width:24px;height:24px;background:#CCFF00;border:2px solid #0A0A0A;box-shadow:0 0 18px rgba(204,255,0,0.6);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}


function isPastSlot(hour, dateStr) {
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (dateStr < today) return true;
  if (dateStr > today) return false;
  const currentHour = d.getHours() + d.getMinutes() / 60;
  return hour < currentHour;
}

function FactCard({ label, value, icon: Icon, accent = "text-volt", children }) {
  return (
    <div className="border border-border/60 bg-card p-4 min-h-[88px]">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
        <Icon className={`h-3.5 w-3.5 ${accent}`} />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-sm font-semibold">{value}</div>
      {children}
    </div>
  );
}

function BookingPanel({
  court,
  date,
  formattedDate,
  setDate,
  selectedDuration,
  setSelectedDuration,
  slots,
  pickedHours,
  toggleHour,
  hasPeakHours,
  hasWeekendPeak,
  bookingRangeLabel,
  bookingTotal,
  selectedSlots,
  totalSelectedMinutes,
  onBook,
  onCreateSession,
  onMessageHost,
  onShare,
  t,
}) {
  const perUnitLabel =
    selectedDuration && selectedDuration !== 60
      ? t("court.per_min", { n: selectedDuration })
      : t("court.per_hour");
  const bookingReady = pickedHours.length > 0;
  const bookingCtaLabel = t("court.continue_to_booking");
  const addressLine = [court.address, court.city].filter(Boolean).join(", ");
  const whatsappNumber = court.facility?.whatsapp_number || court.whatsapp_number;
  const phoneNumber = court.facility?.phone_number || court.phone_number;

  return (
    <div className="border border-border bg-card p-4 md:p-6 space-y-5 overflow-hidden">
      <div className="space-y-3">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.26em] uppercase text-muted-foreground">
              {t("court.booking_panel_eyebrow")}
            </div>
            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
              <span className="font-heading text-4xl md:text-5xl text-volt leading-none">
                {formatPrice(
                  (court.slot_pricing || {})[String(selectedDuration)] ?? court.price_per_slot,
                  court.currency
                )}
              </span>
              <span className="label-eyebrow">/ {perUnitLabel}</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {t("court.booking_panel_helper")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end">
            <span className="inline-flex items-center gap-1.5 border border-border/70 bg-background px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-volt" />
              {court.auto_accept ? t("court.instant_confirm") : t("court.host_approval")}
            </span>
            {court.rating > 0 ? (
              <span className="inline-flex items-center gap-1.5 border border-border/70 bg-background px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                <Star className="h-3.5 w-3.5 fill-volt text-volt" />
                {court.rating} ({court.review_count})
              </span>
            ) : null}
          </div>
        </div>

        {(hasPeakHours || hasWeekendPeak) && (
          <div className="flex flex-wrap items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-volt">
            <Flame className="h-3.5 w-3.5" />
            {hasPeakHours ? (
              <span>
                {t("court.peak_hours")} +{Math.round((court.peak_multiplier - 1) * 100)}%
              </span>
            ) : null}
            {hasPeakHours && hasWeekendPeak ? <span>·</span> : null}
            {hasWeekendPeak ? (
              <span>
                {t("court.weekends")} +{Math.round((court.weekend_multiplier - 1) * 100)}%
              </span>
            ) : null}
          </div>
        )}
      </div>

      <div className="border border-border/70 bg-background/60 p-4 md:p-5 space-y-5 overflow-hidden">
        <div className="space-y-3">
          <DatePicker value={date} onChange={setDate} min={todayStr()} />
        </div>

        <div className="border-t border-border/60 pt-5 space-y-3">
          <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">{t("court.slot_duration")}</div>
          {(() => {
            const durations = court?.slot_durations || [30, 60, 90, 120];
            const pricing = court?.slot_pricing || {};
            let bestDur = durations[0];
            let bestRate = Infinity;
            durations.forEach((d) => {
              const price = pricing[String(d)] ?? court?.price_per_slot ?? 0;
              const rate = price / (d / 60);
              if (rate < bestRate) { bestRate = rate; bestDur = d; }
            });
            return (
              <select
                value={selectedDuration}
                onChange={(e) => setSelectedDuration(Number(e.target.value))}
                className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt"
              >
                {durations.map((dur) => {
                  const durPrice = pricing[String(dur)] ?? court?.price_per_slot;
                  const hours = Math.floor(dur / 60);
                  const mins = dur % 60;
                  const label = hours > 0 && mins > 0 ? `${hours}h ${mins}min` : hours > 0 ? `${hours}h` : `${mins}min`;
                  const isBest = dur === bestDur && durations.length > 1;
                  return (
                    <option key={dur} value={dur}>
                      {label} — {formatPrice(durPrice, court.currency)}{isBest ? ` ★ ${t("court.best_value")}` : ""}
                    </option>
                  );
                })}
              </select>
            );
          })()}
        </div>

        <div className="border-t border-border/60 pt-5 space-y-3">

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-72 overflow-auto pr-1">
            {date && slots.length === 0 ? (
              <div className="col-span-4 flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Clock className="h-6 w-6 mb-2 opacity-50" />
                <span className="text-[10px] tracking-[0.2em] uppercase">{t("play.no_slots")}</span>
              </div>
            ) : (
              slots.map((s) => {
                const picked = pickedHours.includes(s.hour);
                const discountActive = s.has_discount && s.original_price > s.price;
                const showPricing = s.available;
                return (
                  <button
                    key={s.hour}
                    type="button"
                    disabled={!s.available}
                    onClick={() => toggleHour(s.hour, s.available)}
                    className={`py-2 text-xs font-mono border transition-all relative ${
                      !s.available
                        ? "border-red-400/20 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(248,113,113,0.06)_3px,rgba(248,113,113,0.06)_6px)] text-muted-foreground/60 cursor-not-allowed"
                        : picked
                        ? "border-volt bg-volt text-primary-foreground font-bold"
                        : "border-border hover:border-volt/60"
                    }`}
                  >
                    {formatHour(s.hour)}
                    {showPricing ? (
                      <div
                        className={`text-[10px] font-semibold ${
                          picked ? "text-primary-foreground" : "text-foreground"
                        }`}
                      >
                        {discountActive ? (
                          <span>
                            <span className="line-through opacity-50">
                              {formatPrice(s.original_price, court.currency)}
                            </span>{" "}
                            <span className={picked ? "text-primary-foreground" : "text-volt"}>
                              {formatPrice(s.price, court.currency)}
                            </span>
                          </span>
                        ) : (
                          formatPrice(s.price, court.currency)
                        )}
                      </div>
                    ) : null}
                    {showPricing && (s.is_peak || (hasWeekendPeak && s.is_weekend)) && (
                      <Flame className="absolute top-0.5 end-0.5 h-2.5 w-2.5 text-volt opacity-80" />
                    )}
                    {showPricing && discountActive && s.available && (
                      <span className="absolute top-0.5 start-0.5 bg-volt text-primary-foreground text-[7px] leading-none px-0.5 py-[1px] font-bold">
                        -{Math.round((1 - s.price / s.original_price) * 100)}%
                      </span>
                    )}
                    {!s.available && (
                      <span className="absolute top-0.5 start-0.5 bg-red-400/80 text-white text-[7px] leading-none px-1 py-[1px] font-bold tracking-wider">
                        {t("court.booked")}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {(court.owner_name || whatsappNumber || phoneNumber) && (
        <div className="border border-border/70 bg-background/60 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                {t("court.host_support")}
              </div>
              {court.owner_name ? (
                <div className="mt-1 flex items-center gap-2">
                  <Link to={`/player/${court.owner_id}`} className="font-semibold truncate hover:text-volt transition-colors">
                    {court.owner_name}
                  </Link>
                  {court.owner_verified ? (
                    <span className="inline-flex items-center gap-1 text-volt text-[10px] tracking-[0.18em] uppercase">
                      <BadgeCheck className="h-3.5 w-3.5" />
                      {t("court.verified")}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
                      {t("court.unverified")}
                    </span>
                  )}
                </div>
              ) : null}
            </div>
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-volt/15">
              <User className="h-5 w-5 text-volt" />
            </div>
          </div>

          <div className="space-y-2">
            <button
              type="button"
              onClick={onMessageHost}
              className="btn-outline rounded-sm w-full py-3 text-xs flex items-center justify-center gap-2 hover:border-volt hover:text-volt transition-colors"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {t("court.message_host")}
            </button>

            {whatsappNumber ? (
              <a
                href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline rounded-sm w-full py-3 text-xs flex items-center justify-center gap-2 hover:border-green-400 hover:text-green-400 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                {t("court.whatsapp_host")}
              </a>
            ) : null}

            {phoneNumber ? (
              <a
                href={`tel:${phoneNumber}`}
                className="btn-outline rounded-sm w-full py-3 text-xs flex items-center justify-center gap-2 hover:border-volt hover:text-volt transition-colors"
              >
                <Phone className="h-3.5 w-3.5" />
                {t("owner.call")}
              </a>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CourtDetails() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { favoriteIds, toggle } = useFavorites();
  const [court, setCourt] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeImage, setActiveImage] = useState(0);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [pickedHours, setPickedHours] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(60);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [amenitiesList, setAmenitiesList] = useState([]);
  useEffect(() => {
    api.get("/amenities").then(r => setAmenitiesList(r.data)).catch(() => {});
  }, []);

  const loadAvailability = (currentCourt, currentDate, duration) => {
    if (!currentCourt) return;
    const dur = duration || currentCourt.default_slot_duration || currentCourt.slot_duration || 60;
    api
      .get(`/courts/${currentCourt.id}/availability`, { params: { date: currentDate, duration: dur } })
      .then((r) => {
        const now = new Date();
        const today = todayStr();
        const currentHour = now.getHours() + now.getMinutes() / 60;
        const slots = (r.data.slots || []).map((s) => {
          if (currentDate < today) {
            return { ...s, available: false };
          }
          if (currentDate === today && s.hour < currentHour) {
            return { ...s, available: false };
          }
          return s;
        });
        setSlots(slots);
        if (!court) setSelectedDuration(r.data.slot_duration);
      });
  };

  const loadCourtDetails = useCallback(async (currentDate = date) => {
    setLoading(true);
    setLoadError("");
    try {
      const [courtResponse, reviewsResponse] = await Promise.all([
        api.get(`/courts/${id}`),
        api.get(`/courts/${id}/reviews`),
      ]);
      setCourt(courtResponse.data);
      setReviews(reviewsResponse.data);
      const dur =
        courtResponse.data.default_slot_duration || courtResponse.data.slot_duration || 60;
      setSelectedDuration(dur);
      loadAvailability(courtResponse.data, currentDate, dur);
    } catch (e) {
      setLoadError(formatApiError(e.response?.data?.detail) || t("court.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [date, id, t]);

  useEffect(() => {
    loadCourtDetails(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (court) {
      setPickedHours([]);
      loadAvailability(court, date, selectedDuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    if (court) {
      setPickedHours([]);
      loadAvailability(court, date, selectedDuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDuration]);

  const toggleHour = (h, available) => {
    if (!available || isPastSlot(h, date)) return;
    const step = selectedDuration / 60;
    setPickedHours((prev) => {
      if (prev.includes(h)) return prev.filter((x) => x !== h);
      const next = [...prev, h].sort((a, b) => a - b);
      for (let i = 1; i < next.length; i++) {
        if (Math.abs(next[i] - next[i - 1] - step) > 0.001) return [h];
      }
      return next;
    });
  };

  const messageHost = async () => {
    if (!user) {
      toast.error(t("common.sign_in_to_chat"));
      navigate(`/login?redirect=/courts/${id}`);
      return;
    }
    try {
      const r = await api.post("/chats", { court_id: id });
      navigate(`/chats?id=${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleCourtActive = async () => {
    if (!court) return;
    try {
      const r = await api.patch(`/admin/courts/${court.id}`, { active: !court.active });
      setCourt({ ...court, active: r.data.active });
      toast.success(r.data.active ? "Court activated" : "Court paused");
    } catch {
      toast.error("Failed to update court status");
    }
  };

  const submitReview = async () => {
    if (!user) {
      toast.error(t("common.sign_in_to_review"));
      return;
    }
    try {
      await api.post(`/courts/${id}/reviews`, { rating: reviewRating, comment: reviewComment });
      setReviewComment("");
      const r = await api.get(`/courts/${id}/reviews`);
      setReviews(r.data);
      const c = await api.get(`/courts/${id}`);
      setCourt(c.data);
      toast.success(t("common.review_posted"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return <div className="p-10 label-eyebrow animate-pulse">{t("common.loading")}</div>;
  }

  if (loadError || !court) {
    return (
      <div className="bg-background min-h-[calc(100vh-64px)]">
        <div className="max-w-[720px] mx-auto px-5 md:px-10 py-16">
          <BackButton className="mb-6" />
          <div className="border border-border/70 bg-card p-6 md:p-8">
            <div className="label-eyebrow text-volt mb-3">{t("court.could_not_load")}</div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight">
              {t("court.load_failed")}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              {loadError || t("court.load_failed_helper")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={loadCourtDetails} className="btn-volt px-5 py-3 text-xs">
                {t("common.retry")}
              </button>
              <button onClick={() => navigate(-1)} className="btn-outline px-5 py-3 text-xs">
                {t("court.go_back")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasPeakHours = (court.peak_hours || []).length > 0 && (court.peak_multiplier || 1) > 1;
  const hasWeekendPeak = (court.weekend_multiplier || 1) > 1;
  const selectedSlots = slots
    .filter((slot) => pickedHours.includes(slot.hour))
    .sort((a, b) => a.hour - b.hour);
  const bookingStart = selectedSlots[0]?.hour ?? null;
  const bookingEnd =
    selectedSlots.length > 0
      ? selectedSlots[selectedSlots.length - 1].hour + selectedDuration / 60
      : null;
  const bookingRangeLabel =
    bookingStart !== null && bookingEnd !== null
      ? `${formatHour(bookingStart)} - ${formatHour(bookingEnd)}`
      : null;
  const bookingTotal = selectedSlots.reduce((sum, slot) => sum + Number(slot.price || 0), 0);
  const totalSelectedMinutes = pickedHours.length * selectedDuration;
  const facilityName = court.facility?.name;
  const locationLabel = [court.address, court.city].filter(Boolean).join(", ");
  const isFavorite = favoriteIds.includes(court.id);
  const acceptedPaymentMethods = Array.from(new Set((court.accepted_payment_methods || []).filter((m) => m !== "online")));
  const formattedDate = new Intl.DateTimeFormat(
    i18n.language?.startsWith("ar") ? "ar-JO" : "en-US",
    { weekday: "short", month: "short", day: "numeric" }
  ).format(new Date(`${date}T00:00:00`));
  const sortedCancellationTiers = [...(court.cancellation_tiers || [])].sort((a, b) => b.hours_before - a.hours_before);
  const fullRefundTier = sortedCancellationTiers.find((tier) => Number(tier.refund_percent) === 100);
  const cancellationSummary = sortedCancellationTiers.length > 0
    ? (
        fullRefundTier
          ? t("court.cancellation_summary_full_until", { hours: fullRefundTier.hours_before })
          : t("court.cancellation_summary_tiered")
      )
    : court.cancellation_hours > 0
      ? t("court.cancellation_summary_full_until", { hours: court.cancellation_hours })
      : t("court.cancellation_summary_none");
  const cancellationTimeline = sortedCancellationTiers.length > 0
    ? sortedCancellationTiers.map((tier, index, arr) => {
        const isLast = index === arr.length - 1;
        const percent = Number(tier.refund_percent || 0);
        let tone = "border-red-500/20 bg-red-500/8 text-red-400";
        if (percent === 100) tone = "border-emerald-500/20 bg-emerald-500/8 text-emerald-400";
        else if (percent > 0) tone = "border-amber-500/20 bg-amber-500/8 text-amber-400";

        let label;
        if (percent === 100) {
          label = t("court.cancel_tier_full", { hours: tier.hours_before });
        } else if (percent === 0 && isLast) {
          label = t("court.cancel_tier_none", { hours: tier.hours_before });
        } else if (percent === 0) {
          label = t("court.cancel_tier_after");
        } else {
          label = t("court.cancel_tier_partial", { percent, hours: tier.hours_before });
        }

        return {
          key: `tier-${index}-${tier.hours_before}-${percent}`,
          percent,
          label,
          windowLabel: `${tier.hours_before}h+`,
          tone,
        };
      })
    : court.cancellation_hours > 0
      ? [
          {
            key: "full-refund",
            percent: 100,
            label: t("court.cancel_tier_full", { hours: court.cancellation_hours }),
            windowLabel: `${court.cancellation_hours}h+`,
            tone: "border-emerald-500/20 bg-emerald-500/8 text-emerald-400",
          },
          {
            key: "late-cancel",
            percent: Math.max(0, 100 - Number(court.cancellation_fee_percent || 0)),
            label: t("court.cancellation_refund_within", {
              percent: Math.max(0, 100 - Number(court.cancellation_fee_percent || 0)),
              hours: court.cancellation_hours,
            }),
            windowLabel: `< ${court.cancellation_hours}h`,
            tone:
              Math.max(0, 100 - Number(court.cancellation_fee_percent || 0)) > 0
                ? "border-amber-500/20 bg-amber-500/8 text-amber-400"
                : "border-red-500/20 bg-red-500/8 text-red-400",
          },
        ]
      : [
          {
            key: "no-refund",
            percent: 0,
            label: t("court.cancel_no_refund"),
            windowLabel: t("court.cancellation_now"),
            tone: "border-red-500/20 bg-red-500/8 text-red-400",
          },
        ];

  const proceedToBooking = () => {
    if (!bookingRangeLabel) return;
    navigate(
      `/courts/${court.id}/book?date=${date}&duration=${selectedDuration}&start=${bookingStart}&end=${bookingEnd}`
    );
  };

  const handleBook = () => {
    if (user && !user.phone_verified) {
      setShowPhoneModal(true);
      return;
    }
    proceedToBooking();
  };

  const handleCreateSessionFromCourt = () => {
    if (!bookingRangeLabel) return;
    navigate(
      `/play/create?court_id=${court.id}&date=${date}&start=${bookingStart}&end=${bookingEnd}&duration=${selectedDuration}`
    );
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("court.link_copied"));
    } catch {
      toast.error(t("court.share_failed"));
    }
  };

  return (
    <div className="bg-background min-h-[calc(100vh-64px)] pb-20 md:pb-20 overflow-x-hidden" data-testid="court-details-page">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-10 py-8">
        <BackButton className="mb-4" />
        <div className="label-eyebrow text-volt mb-2 flex flex-wrap items-center gap-2">
          <span>{t("home.sports." + court.sport)}</span>
          {court.facility && (
            <Link to={`/facilities/${court.facility_id}`} className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground/60 hover:text-volt transition-colors">
              <Building2 className="h-3 w-3" /> {court.facility.name}
            </Link>
          )}
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h1 className="font-heading text-4xl md:text-7xl tracking-tight break-words">{court.name}</h1>
          </div>
          <div className="flex items-center gap-4 text-sm flex-wrap">
            {court.rating > 0 && (
              <span className="flex items-center gap-1 font-semibold">
                <Star className="h-4 w-4 fill-volt text-volt" />
                {court.rating} <span className="text-muted-foreground">({court.review_count})</span>
              </span>
            )}
            <button
              type="button"
              onClick={() => toggle(court.id)}
              className="inline-flex items-center gap-2 border border-border/70 bg-card px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-volt/50 transition-colors"
            >
              <Heart
                className={`h-4 w-4 transition-colors ${
                  isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground"
                }`}
              />
              {isFavorite ? t("court.saved") : t("court.save")}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 border border-border/70 bg-card px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-volt/50 transition-colors"
            >
              <Share2 className="h-4 w-4" />
              {t("court.share")}
            </button>
            {user && (user.role === "admin" || user.id === court.owner_id) && (
              <Link
                to={`/owner/courts/${court.id}/edit`}
                className="inline-flex items-center gap-2 border border-border/70 bg-card px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-volt/50 transition-colors"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Link>
            )}
            {user?.role === "admin" && (
              <button
                type="button"
                onClick={toggleCourtActive}
                className={`inline-flex items-center gap-2 border px-3 py-2 text-xs transition-colors ${
                  court.active
                    ? "border-volt/50 bg-card text-volt hover:bg-volt/10"
                    : "border-muted-foreground/30 bg-card text-muted-foreground hover:text-foreground hover:border-volt/50"
                }`}
                title={court.active ? "Pause court" : "Activate court"}
              >
                {court.active ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
                {court.active ? "Active" : "Paused"}
              </button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-[minmax(0,1fr)_440px] gap-6 items-start">
          <div>
            <div className="aspect-[16/10] overflow-hidden border border-border">
              <img
                src={getImageUrl(court.images[activeImage] || court.images[0], 'large')}
                alt={court.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {court.images.slice(0, 3).map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-[16/6] overflow-hidden border ${
                    activeImage === i ? "border-volt" : "border-border"
                  } transition-all`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={getImageUrl(img, 'thumbnail')} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            <div className="space-y-4 md:hidden mt-6">
              <BookingPanel
                court={court}
                date={date}
                formattedDate={formattedDate}
                setDate={setDate}
                selectedDuration={selectedDuration}
                setSelectedDuration={setSelectedDuration}
                slots={slots}
                pickedHours={pickedHours}
                toggleHour={toggleHour}
                hasPeakHours={hasPeakHours}
                hasWeekendPeak={hasWeekendPeak}
                bookingRangeLabel={bookingRangeLabel}
                bookingTotal={bookingTotal}
                selectedSlots={selectedSlots}
                totalSelectedMinutes={totalSelectedMinutes}
                onBook={handleBook}
                onCreateSession={handleCreateSessionFromCourt}
                onMessageHost={messageHost}
                onShare={handleShare}
                t={t}
              />
            </div>

            <div className="mt-8 space-y-12">
              <section>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="font-heading text-3xl">{t("court.the_court")}</h2>
                  {court.court_type ? (
                    <span className="inline-flex items-center gap-2 border border-border/70 bg-card px-3 py-1.5 text-xs text-muted-foreground">
                      <span className="text-volt font-bold text-sm">⊞</span>
                      {t(
                        `owner.court_form.court_type_${court.court_type.replace(/-/g, "_")}`,
                        court.court_type
                      )}
                    </span>
                  ) : null}
                </div>
                <p className="text-muted-foreground leading-relaxed">{court.description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                  <FactCard
                    label={t("court.fact_confirmation")}
                    value={court.auto_accept ? t("court.instant_confirm") : t("court.host_approval")}
                    icon={ShieldCheck}
                  >
                    <p className="mt-1 text-xs text-muted-foreground">
                      {court.auto_accept ? t("court.confirmation_instant_helper") : t("court.confirmation_host_helper")}
                    </p>
                  </FactCard>

                  <FactCard
                    label={t("court.fact_surface")}
                    value={court.court_surface
                      ? t(
                          `owner.court_form.surface_${court.sport}_${court.court_surface}`,
                          court.court_surface.replace(/_/g, " ")
                        )
                      : t("court.not_available")}
                    icon={court.indoor ? Building2 : Sun}
                  />

                  <FactCard
                    label={t("court.fact_environment")}
                    value={court.indoor ? t("court.indoor") : t("court.outdoor")}
                    icon={court.indoor ? Building2 : Sun}
                  />
                </div>
              </section>

              <section>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <h2 className="font-heading text-3xl">{t("court.amenities")}</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {court.amenities?.map((aid) => {
                    const a = amenitiesList.find(x => x.id === aid);
                    const name = a ? (i18n.language?.startsWith("ar") ? a.nameAr : a.nameEn) : aid;
                    return (
                      <div
                        key={aid}
                        className="flex items-center gap-2 px-4 py-3 border border-border/30 bg-card"
                        data-testid={`amenity-${aid}`}
                      >
                        {a ? <AmenityIcon name={a.icon} className="h-4 w-4 text-volt" /> : <Check className="h-4 w-4 text-volt" />}
                        <span className="text-sm">{name}</span>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <h2 className="font-heading text-3xl">{t("court.accepted_payments")}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {acceptedPaymentMethods.map((method) => {
                    const config = {
                      cod: {
                        icon: Banknote,
                        label: t("court.pay_cod"),
                      },
                      online: {
                        icon: CreditCard,
                        label: t("court.pay_online"),
                      },
                      cliq: {
                        icon: CreditCard,
                        label: t("court.pay_cliq"),
                      },
                      efawateercom: {
                        icon: Landmark,
                        label: t("court.pay_efawateercom"),
                      },
                    }[method];

                    if (!config) return null;

                    const Icon = config.icon;
                    return (
                      <div
                        key={method}
                        className="flex items-center gap-3 px-4 py-4 border border-border/30 bg-card"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-volt/10">
                          <Icon className="h-5 w-5 text-volt" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{config.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {court.extras && court.extras.length > 0 ? (
                <section>
                  <h2 className="font-heading text-3xl mb-5">{t("court.extras")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {court.extras.map((extra, i) => (
                      <div
                        key={i}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border border-border/30 bg-card gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{extra.name}</div>
                          {extra.description && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">{extra.description}</div>
                          )}
                          {extra.max_qty > 1 && (
                            <div className="text-[9px] text-muted-foreground/60 tracking-wider mt-0.5">
                              {t("court.extras_qty_range", { min: extra.min_qty ?? 0, max: extra.max_qty })}
                            </div>
                          )}
                        </div>
                        <div className="text-start sm:text-end">
                          <div className="text-volt font-bold text-sm break-words">
                            {extra.price > 0 ? `${formatPrice(extra.price, court.currency)}${extra.max_qty > 1 ? t("court.extras_each") : ""}` : t("court.extras_free")}
                          </div>
                          <div className="text-[9px] text-muted-foreground/60 tracking-wider">
                            {extra.optional ?? extra.min_qty === 0 ? t("court.extras_optional") : t("court.extras_included")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              <section>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <h2 className="font-heading text-3xl">{t("court.location")}</h2>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${court.lat},${court.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline rounded-sm w-full sm:w-auto px-3 py-2 text-xs flex items-center justify-center gap-2"
                  >
                    <Navigation className="h-3.5 w-3.5" /> {t("court.navigate")}
                  </a>
                </div>

                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px] mb-4">
                  <div className={`${isDark ? "dark-map" : "light-map"} h-72 border border-border overflow-hidden`}>
                    <MapContainer
                      center={[court.lat, court.lng]}
                      zoom={14}
                      scrollWheelZoom={false}
                      style={{ height: "100%", width: "100%", background: isDark ? "#0A0A0A" : "#f8f8f8", zIndex: 0 }}
                    >
                      <TileLayer
                        attribution='&copy; OpenStreetMap'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[court.lat, court.lng]} icon={voltIcon} />
                    </MapContainer>
                  </div>

                  <div className="space-y-3">
                    <div className="border border-border/70 bg-card p-4">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                        {t("court.fact_location")}
                      </div>
                      <div className="mt-2 text-sm font-semibold">{locationLabel}</div>
                      <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                        {t("court.location_helper")}
                      </p>
                    </div>

                    {(facilityName || court.owner_name) && (
                      <div className="border border-border/70 bg-card p-4">
                        <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                          {t("court.trust_section_title")}
                        </div>
                        {facilityName ? (
                          <div className="mt-2 flex items-center gap-2 text-sm font-semibold">
                            <Building2 className="h-4 w-4 text-volt" />
                            {facilityName}
                          </div>
                        ) : null}
                        {court.owner_name ? (
                          <div className="mt-2 flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 text-volt" />
                            <Link to={`/player/${court.owner_id}`} className="font-medium hover:text-volt transition-colors">
                              {court.owner_name}
                            </Link>
                            {court.owner_verified ? (
                              <span className="inline-flex items-center gap-1 text-volt text-[10px] tracking-[0.18em] uppercase">
                                <BadgeCheck className="h-3.5 w-3.5" />
                                {t("court.verified")}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                          {t("court.trust_section_helper")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-start gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-volt/10 flex items-center justify-center shrink-0">
                    <ShieldCheck className="h-5 w-5 text-volt" />
                  </div>
                  <div>
                    <h2 className="font-heading text-3xl">{t("court.cancellation_policy_title")}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{t("court.cancellation_policy_helper")}</p>
                  </div>
                </div>

                <div className="border border-border/70 bg-card p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                        {t("court.cancellation_timeline_label")}
                      </div>
                      <div className="mt-2 text-base sm:text-lg font-semibold text-foreground break-words">{cancellationSummary}</div>
                    </div>
                    <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-border/70 px-3 py-1.5 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 text-volt" />
                      <span className="break-words">{t("court.cancellation_refund_max", { percent: cancellationTimeline[0]?.percent ?? 0 })}</span>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3">
                    {cancellationTimeline.map((item) => (
                      <div key={item.key} className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 border border-border/50 bg-background/40 p-4">
                        <div className={`w-fit rounded-full border px-3 py-2 text-center ${item.tone}`}>
                          <div className="text-base font-bold leading-none">{item.percent}%</div>
                          <div className="mt-1 text-[9px] tracking-[0.2em] uppercase">{t("court.cancellation_refund_badge")}</div>
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground">{item.label}</div>
                        </div>

                        <div className="w-fit rounded-full border border-border/60 px-3 py-1.5 text-xs text-muted-foreground">
                          {item.windowLabel}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {(court.weather_cancellation || court.no_show_keep_deposit) && (
                  <div className="border border-border/70 bg-card p-4 mt-4">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">
                      {t("court.cancellation_notes_label")}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {court.weather_cancellation && (
                        <div className="inline-flex max-w-full items-center gap-2 px-3 py-1.5 bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs tracking-wider">
                          <Sun className="h-3.5 w-3.5 shrink-0" />
                          <span className="break-words">{t("court.weather_protection")}</span>
                        </div>
                      )}
                      {court.no_show_keep_deposit && (
                        <div className="inline-flex max-w-full items-center gap-2 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 text-violet-400 text-xs tracking-wider">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          <span className="break-words">{t("court.no_show_protection")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>

              <section>
                <h2 className="font-heading text-3xl mb-5">{t("court.rules")}</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">{(court.facility?.rules || court.rules) || t("court.rules_default")}</p>
              </section>

              <section>
                <h2 className="font-heading text-3xl mb-5">
                  {t("court.reviews")} ({reviews.length})
                </h2>
                <div className="space-y-4 mb-8">
                  {reviews.length > 0 ? (
                    reviews.map((r) => (
                      <div key={r.id} className="border border-border/30 bg-card p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">{r.user_name}</span>
                          <span className="flex items-center gap-1 text-xs">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star
                                key={i}
                                className={`h-3 w-3 ${
                                  i < r.rating ? "fill-volt text-volt" : "text-muted-foreground/40"
                                }`}
                              />
                            ))}
                          </span>
                        </div>
                        <p className="text-muted-foreground text-sm">{r.comment}</p>
                      </div>
                    ))
                  ) : (
                    <div className="border border-border/30 bg-card p-5 text-sm text-muted-foreground">
                      {t("court.no_reviews")}
                    </div>
                  )}
                </div>

                {user && user.role !== "admin" && (
                  <div className="border border-border bg-card p-5">
                    <div className="label-eyebrow mb-3">{t("court.leave_review")}</div>
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          data-testid={`review-star-${n}`}
                          onClick={() => setReviewRating(n)}
                          type="button"
                        >
                          <Star
                            className={`h-5 w-5 ${
                              n <= reviewRating ? "fill-volt text-volt" : "text-muted-foreground/40"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    <textarea
                      data-testid="review-comment-input"
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder={t("court.review_placeholder")}
                      rows={3}
                      className="w-full bg-background border border-input p-3 text-sm focus:border-volt focus:outline-none"
                    />
                    <button
                      data-testid="submit-review-btn"
                      onClick={submitReview}
                      className="btn-volt mt-3 px-5 py-2 text-xs"
                    >
                      {t("court.post_review")}
                    </button>
                  </div>
                )}
              </section>
            </div>
          </div>

          <div className="space-y-4 hidden md:block">
            <aside className="md:sticky md:top-20 self-start">
              <BookingPanel
                court={court}
                date={date}
                formattedDate={formattedDate}
                setDate={setDate}
                selectedDuration={selectedDuration}
                setSelectedDuration={setSelectedDuration}
                slots={slots}
                pickedHours={pickedHours}
                toggleHour={toggleHour}
                hasPeakHours={hasPeakHours}
                hasWeekendPeak={hasWeekendPeak}
                bookingRangeLabel={bookingRangeLabel}
                bookingTotal={bookingTotal}
                selectedSlots={selectedSlots}
                totalSelectedMinutes={totalSelectedMinutes}
                onBook={handleBook}
                onCreateSession={handleCreateSessionFromCourt}
                onMessageHost={messageHost}
                onShare={handleShare}
                t={t}
              />
            </aside>
          </div>
        </div>
      </div>

      {/* Desktop sticky footer */}
      <div className="hidden md:flex fixed bottom-0 start-0 end-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border items-center justify-between px-6 py-3 gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="flex items-baseline gap-2 whitespace-nowrap">
            <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">{t("court.total")}</span>
            <span className="font-heading text-2xl text-volt">
              {bookingRangeLabel ? formatPrice(bookingTotal, court.currency) : "—"}
            </span>
            {totalSelectedMinutes > 0 && (
              <span className="text-xs text-muted-foreground">
                / {Math.floor(totalSelectedMinutes / 60) > 0 && `${Math.floor(totalSelectedMinutes / 60)}h`}{totalSelectedMinutes % 60 > 0 ? ` ${totalSelectedMinutes % 60}min` : ""}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            {bookingRangeLabel ? `${formattedDate} · ${bookingRangeLabel}` : t("court.summary_slot_placeholder")}
          </div>
        </div>
        <button
          onClick={handleBook}
          disabled={!bookingRangeLabel}
          className="btn-volt px-5 py-2.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed shrink-0 ml-auto"
        >
          {bookingRangeLabel ? t("court.continue_to_booking") : t("court.mobile_pick_prompt")}
        </button>
      </div>

      {/* Mobile sticky footer */}
      <div className="md:hidden fixed bottom-0 start-0 end-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border px-3 py-3">
        <div className="flex flex-col gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">{t("court.total")}</span>
              <span className="font-heading text-xl sm:text-2xl text-volt">
                {bookingRangeLabel ? formatPrice(bookingTotal, court.currency) : "—"}
              </span>
              {totalSelectedMinutes > 0 && (
                <span className="text-xs text-muted-foreground">
                  / {Math.floor(totalSelectedMinutes / 60) > 0 && `${Math.floor(totalSelectedMinutes / 60)}h`}{totalSelectedMinutes % 60 > 0 ? ` ${totalSelectedMinutes % 60}min` : ""}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1 break-words">
              {bookingRangeLabel ? `${formattedDate} · ${bookingRangeLabel}` : t("court.summary_slot_placeholder")}
            </div>
          </div>
          <button
            onClick={handleBook}
            disabled={!bookingRangeLabel}
            className="btn-volt w-full py-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {bookingRangeLabel ? t("court.continue_to_booking") : t("court.mobile_pick_prompt")}
          </button>
        </div>
      </div>

      {showPhoneModal && (
        <VerifyPhone
          variant="modal"
          onVerified={() => {
            setShowPhoneModal(false);
            proceedToBooking();
          }}
          onClose={() => setShowPhoneModal(false)}
        />
      )}
    </div>
  );
}
